import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { IsoPoint, PlacedBlock, IsomPart, DrawingElement } from '@/app/types/cad';
import { createDefaultTransform } from '@/app/utils/transform';

interface PipeCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPipeCreate: (pipe: PlacedBlock) => void;
  startPoint?: IsoPoint;
  endPoint?: IsoPoint;
}

export function PipeCreateDialog({
  open,
  onOpenChange,
  onPipeCreate,
  startPoint,
  endPoint,
}: PipeCreateDialogProps) {
  const [dn, setDn] = useState('100');
  const [length, setLength] = useState('5');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState('2');

  if (!open) return null;

  const handleCreate = () => {
    if (!startPoint || !endPoint) return;

    const du = endPoint.u - startPoint.u;
    const dv = endPoint.v - startPoint.v;

    // Calculate actual length in grid units
    const actualLength = Math.sqrt(du * du + dv * dv);
    
    // Calculate length in mm (assuming 200mm grid size - should match background settings)
    const gridSizeMm = 200; // TODO: Get from context or props
    const lengthInMm = actualLength * gridSizeMm;

    const elements = [
      // Line
      {
        id: `line-${Date.now()}`,
        type: 'line',
        start: { u: 0, v: 0 },
        end: { u: du, v: dv },
        color: color,
        width: lineWidth,
      },
      // Start socket at origin
      {
        id: `socket-start-${Date.now()}`,
        type: 'connection',
        position: { u: 0, v: 0 },
        color: color,
        fillType: 'ring',
        diameter: 50,
        strokeWidth: 5,
        label: '',
      },
      // End socket at relative end position
      {
        id: `socket-end-${Date.now()}`,
        type: 'connection',
        position: { u: du, v: dv },
        color: color,
        fillType: 'ring',
        diameter: 50,
        strokeWidth: 5,
        label: '',
      },
      // Automatic dimension
      {
        id: `dimension-${Date.now()}`,
        type: 'dimension',
        start: { u: 0, v: 0 },
        end: { u: du, v: dv },
        value: lengthInMm.toFixed(1),
        unit: 'mm',
        showUnit: false,
        decimalPlaces: 0,
        offset: 0.5,
        color: '#000000',
        textSize: 50,
        lineWidth: 2.5,
        showLine: false,
        showArrows: false,
        arrowSize: 8,
        arrowFilled: false,
        arrowPosition: 'inside',
      },
    ];

    // Create IsomPart
    const pipeData: IsomPart = {
      name: `Leitung DN${dn}`,
      elements: elements,
      attributes: [
        {
          id: `attr-dn-${Date.now()}`,
          name: 'DN',
          value: parseInt(dn),
          unit: 'mm',
          type: 'TGA',
        },
        {
          id: `attr-length-${Date.now()}`,
          name: 'Länge',
          value: Number(lengthInMm.toFixed(2)),
          unit: 'mm',
          type: 'length',
        },
      ],
    };

    // Create PlacedBlock at start point
    const pipe: PlacedBlock = {
      id: `pipe-${Date.now()}`,
      blockData: pipeData,
      transform: createDefaultTransform(startPoint),
    };

    onPipeCreate(pipe);
    onOpenChange(false);

    // Reset form
    setDn('100');
    setLength('5');
    setColor('#000000');
    setLineWidth('2');
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-96 rounded-lg shadow-lg"
        style={{
          backgroundColor: 'var(--md-surface-container-high)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <h2
            className="text-lg font-medium"
            style={{ color: 'var(--md-on-surface)' }}
          >
            Leitung erstellen
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="rounded-full w-8 h-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--md-on-surface)' }}
            >
              DN (Nennweite)
            </label>
            <select
              value={dn}
              onChange={(e) => setDn(e.target.value)}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            >
              <option value="15">DN 15</option>
              <option value="20">DN 20</option>
              <option value="25">DN 25</option>
              <option value="32">DN 32</option>
              <option value="40">DN 40</option>
              <option value="50">DN 50</option>
              <option value="65">DN 65</option>
              <option value="80">DN 80</option>
              <option value="100">DN 100</option>
              <option value="125">DN 125</option>
              <option value="150">DN 150</option>
              <option value="200">DN 200</option>
              <option value="250">DN 250</option>
              <option value="300">DN 300</option>
            </select>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--md-on-surface)' }}
            >
              Länge (Grid-Einheiten)
            </label>
            <input
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              min="1"
              step="1"
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--md-on-surface)' }}
            >
              Farbe
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 rounded border cursor-pointer"
                style={{
                  borderColor: 'var(--md-outline)',
                }}
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 px-3 py-2 rounded border font-mono"
                style={{
                  backgroundColor: 'var(--md-surface-container)',
                  borderColor: 'var(--md-outline)',
                  color: 'var(--md-on-surface)',
                }}
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--md-on-surface)' }}
            >
              Linienbreite
            </label>
            <input
              type="number"
              value={lineWidth}
              onChange={(e) => setLineWidth(e.target.value)}
              min="0.5"
              step="0.5"
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline)',
                color: 'var(--md-on-surface)',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 p-4 border-t"
          style={{ borderColor: 'var(--md-outline-variant)' }}
        >
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleCreate}>Erstellen</Button>
        </div>
      </div>
    </>
  );
}